import { inject, Injectable } from '@angular/core';
import { MpsUserService } from '@mps/services/mps-user.service';
import { DEFAULT_PROFILE, Profile, PROFILES, ProfileType, STORAGE_KEY } from '../models/profile.model';

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  private userService = inject(MpsUserService);
  private currentProfile = DEFAULT_PROFILE as ProfileType;

  getCurrentProfile(): ProfileType {
    return this.currentProfile;
  }

  getCurrentProfileData(): Profile {
    const currentId = this.getCurrentProfile();
    return PROFILES.find((p) => p.id === currentId) || PROFILES[0];
  }

  setCurrentProfile(profile: ProfileType): void {
    this.currentProfile = profile;
    this.saveToStorage(profile);
  }

  isHpcUser(): boolean {
    return this.getCurrentProfile() === 'hpg';
  }

  /**
   * Admin du back-office (b_admin) : seul autorisé par l'API sur les routes "admin" (imports, uploads, édition HPC).
   * Basé sur b_admin et non sur MpsUser.isAdmin() (role_id), comme le middleware "admin" de l'API.
   */
  isAdminUser(): boolean {
    return !!this.userService.getUser()?.b_admin;
  }

  /**
   * Import HR4U : le nom du fichier doit commencer par le tag de la population du profil courant
   * (HPC_, SE_, N17_, sans tenir compte de la casse), pour éviter d'importer une population à la place d'une autre.
   * Même règle côté API (ImportHR4uRequest).
   */
  hasExpectedFilePrefix(fileName: string): boolean {
    const prefix = this.getCurrentProfileData().filePrefix;
    return !!prefix && fileName.toUpperCase().startsWith(prefix.toUpperCase());
  }

  /**
   * Le potentiel n'est visible qu'en profil HPC, et uniquement pour les membres COMEX / RH
   */
  canViewPotentiel(): boolean {
    const user = this.userService.getUser();
    return this.isHpcUser() && !!(user?.b_comex || user?.b_rh);
  }

  /**
   * Charge depuis localStorage
   */
  public loadFromStorage(): ProfileType {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && ['hpg', 'cd', 'n17'].includes(stored)) {
        return stored as ProfileType;
      } else {
        return DEFAULT_PROFILE;
      }
    } catch (error) {
      console.warn('Erreur chargement profil:', error);
      return DEFAULT_PROFILE;
    }
  }

  /**
   * Sauvegarde dans localStorage
   */
  private saveToStorage(profile: ProfileType): void {
    try {
      localStorage.setItem(STORAGE_KEY, profile);
    } catch (error) {
      console.warn('Erreur sauvegarde profil:', error);
    }
  }
}
